import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, Square } from 'lucide-react';
import styles from './AgentChat.module.css';
import { ChatMessage, ToolCall } from '../../services/teachingAgent';
import {
  MessageActions,
  ReplySkeleton,
  SourceChips,
  StreamNotice,
  ThinkingTrace,
  TutorMarkdown,
  useSlowAfter,
  useTutorStatusText,
} from '../TutorChat';
import type { TraceState } from '../TutorChat';

interface AgentChatProps {
  messages: ChatMessage[];
  isStreaming: boolean;
  error: Error | null;
  onSuggestionClick?: (suggestion: string) => void;
  /** Tool the agent is running right now; drives the human status line. */
  currentTool?: ToolCall | null;
  isThinking?: boolean;
  /** Teaching session id, forwarded with feedback as context. */
  sessionId?: string | null;
  /** Stop the in-flight reply. */
  onStop?: () => void;
  /** Re-ask the last question. Shown under the latest finished reply. */
  onRegenerate?: () => void;
  /** Live thinking trace for the latest turn (from useTeachingAgent). */
  trace?: TraceState;
}

const SUGGESTION_KEYS = ['async', 'code', 'nn', 'error'] as const;
const MessageBubble: React.FC<{
  message: ChatMessage;
  isStreaming: boolean;
  isLatest: boolean;
  currentTool?: ToolCall | null;
  onStop?: () => void;
  onRegenerate?: () => void;
  sessionId?: string | null;
  trace?: TraceState;
}> = ({ message, isStreaming, isLatest, currentTool, onStop, onRegenerate, sessionId, trace }) => {
  const { t } = useTranslation();
  const status = useTutorStatusText(
    isLatest && isStreaming && message.role === 'assistant',
    message.content !== '',
    currentTool,
  );
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';
  const live = isLatest && isStreaming && isAssistant;
  const awaitingFirstToken = live && message.content === '';
  const slow = useSlowAfter(awaitingFirstToken);
  const showTrace = isAssistant && isLatest && !!trace && trace.phase !== 'idle';
  // Stopped is recorded on the message itself (survives a new turn) and on the
  // live trace; either one shows the single StreamNotice.
  const stopped =
    isAssistant && (message.metadata?.stopped === true || (showTrace && trace!.phase === 'stopped'));
  // The trace already says "Thinking..."; do not show the status line's twin.
  const statusText = showTrace && live ? '' : status;
  const time = new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  return (
    <article
      className={`${styles.message} ${styles[message.role]}`}
      aria-label={isUser ? t('tutor.you') : t('companion.name')}
    >
      <header className={styles.messageHeader}>
        {isAssistant && <span className={styles.avatar} aria-hidden>A</span>}
        <span className={styles.messageRole}>{isUser ? t('tutor.you') : t('companion.name')}</span>
        <time className={styles.messageTime} dateTime={new Date(message.timestamp).toISOString()}>{time}</time>
      </header>

      {showTrace && <ThinkingTrace trace={trace!} />}

      <div className={styles.messageContent} data-live={live && message.content !== '' ? 'true' : undefined}>
        {awaitingFirstToken && <ReplySkeleton />}
        {isUser || message.role === 'system' ? (
          <p className={styles.plain}>{message.content}</p>
        ) : (
          message.content !== '' && <TutorMarkdown content={message.content} streaming={live} />
        )}
      </div>

      {(statusText || (live && onStop)) && (
        <div className={styles.statusLine}>
          <span className={styles.pulse} aria-hidden />
          <span>{statusText}</span>
          {onStop && (
            <button type="button" className={styles.stopBtn} onClick={onStop}>
              <Square size={11} aria-hidden />
              {t('tutor.stop')}
            </button>
          )}
        </div>
      )}

      {slow && <StreamNotice kind="slow" />}
      {stopped && !isStreaming && (
        <StreamNotice kind="stopped" onRetry={isLatest ? onRegenerate : undefined} />
      )}

      {isAssistant && message.metadata?.contentType === 'question' && (
        <div className={styles.questionBadge}>{t('tutor.thinking_question')}</div>
      )}

      {isAssistant && !live && message.content !== '' && (
        <>
          <SourceChips sources={message.metadata?.sources} />
          <MessageActions
            messageId={message.metadata?.messageId}
            sessionId={sessionId}
            text={message.content}
            onRegenerate={isLatest && !isStreaming ? onRegenerate : undefined}
          />
        </>
      )}
    </article>
  );
};

const AgentChat: React.FC<AgentChatProps> = ({
  messages,
  isStreaming,
  error,
  onSuggestionClick,
  currentTool,
  onStop,
  onRegenerate,
  sessionId,
  trace,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const pinnedToBottomRef = useRef(true);
  const [newReplies, setNewReplies] = useState(false);
  const hasMessages = messages.length > 0;

  // Both CompanionChat and TeachingSidebar put AgentChat directly inside the
  // message scroller. Manual reading takes precedence over incoming replies.
  useEffect(() => {
    const host = containerRef.current?.parentElement;
    if (!host) return undefined;
    const updatePinned = () => {
      pinnedToBottomRef.current = host.scrollHeight - host.scrollTop - host.clientHeight < 48;
      if (pinnedToBottomRef.current) setNewReplies(false);
    };
    host.addEventListener('scroll', updatePinned, { passive: true });
    return () => host.removeEventListener('scroll', updatePinned);
  }, [hasMessages]);

  // Instant (not smooth) follow while streaming: animated scrolling fights the
  // per-token growth and makes the text jump.
  useEffect(() => {
    const host = containerRef.current?.parentElement;
    if (!host) return;
    if (pinnedToBottomRef.current) host.scrollTop = host.scrollHeight;
    else setNewReplies(true);
  }, [messages]);

  const jumpToLatest = () => {
    const host = containerRef.current?.parentElement;
    if (host) host.scrollTop = host.scrollHeight;
    pinnedToBottomRef.current = true;
    setNewReplies(false);
  };

  if (messages.length === 0 && !error) {
    return (
      <div className={styles.emptyState}>
        <span className={styles.welcomeMark} aria-hidden>A</span>
        <h3>{t('tutor.empty_title')}</h3>
        <p>{t('tutor.empty_body')}</p>
        <div className={styles.suggestions}>
          <p className={styles.suggestionsTitle}>{t('tutor.try_asking')}</p>
          <div className={styles.suggestionList}>
            {SUGGESTION_KEYS.map((key) => {
              const text = t(`tutor.suggestion.${key}`);
              return (
                <button
                  key={key}
                  type="button"
                  className={styles.suggestionPill}
                  onClick={() => onSuggestionClick?.(text)}
                >
                  {text}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={styles.chatContainer}
      ref={containerRef}
      role="log"
      aria-live="polite"
      aria-relevant="additions"
      aria-busy={isStreaming}
      aria-label={t('tutor.conversation_label', { name: t('companion.name') })}
    >
      {messages.map((message, index) => (
        <MessageBubble
          key={message.id}
          message={message}
          isStreaming={isStreaming}
          isLatest={index === messages.length - 1}
          currentTool={currentTool}
          onStop={onStop}
          onRegenerate={onRegenerate}
          sessionId={sessionId}
          trace={trace}
        />
      ))}

      {error && (
        <div className={styles.errorMessage} role="alert">
          {error.message}
        </div>
      )}

      {newReplies && (
        <button type="button" className={styles.newReplies} onClick={jumpToLatest}>
          <ArrowDown size={13} aria-hidden />
          {t('tutor.jump_latest')}
        </button>
      )}
    </div>
  );
};

export default AgentChat;
